import {
  useGetIngestScratchpadQuery,
  useGetIngestSectionScratchpadQuery
} from '@axilon-apps/domains-ontology-ui-state'
import { Card, CardContent } from '../../../components/ui/card'
import { Skeleton } from '../../../components/ui/skeleton'
import { LazyChatMarkdown } from '../../../components/ChatMarkdown/LazyChatMarkdown'

interface ScratchpadProps {
  jobId: string
  /** Show one section's notes instead of the lead overview; null = job-level. */
  sectionId?: string | null
}

/**
 * The agents' durable, user-facing notes rendered as markdown: the lead's
 * corpus overview on "All", or — when a section tab is selected — that
 * section's own scratchpad. The narrative surface, distinct from the
 * branch-derived "What it built" graph. Scratchpad writes publish nothing, so
 * it refreshes on the `jobs` wake-ups the same run produces.
 */
export function Scratchpad({ jobId, sectionId = null }: ScratchpadProps) {
  const jobLevel = useGetIngestScratchpadQuery(jobId, {
    skip: !jobId || sectionId !== null,
    refetchOnMountOrArgChange: true
  })
  const sectionLevel = useGetIngestSectionScratchpadQuery(
    { jobId, sectionId: sectionId ?? '' },
    {
      skip: !jobId || sectionId === null,
      refetchOnMountOrArgChange: true
    }
  )
  const { data, isLoading, isError } = sectionId !== null ? sectionLevel : jobLevel

  const content = data?.content?.trim() ?? ''

  return (
    <Card>
      <CardContent className="min-w-0">
        {isLoading && !data ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-4/5" />
          </div>
        ) : content ? (
          <div className="chat-markdown doc-markdown min-w-0 break-words [&>*:first-of-type]:mt-0 [&>*:last-child]:mb-0">
            <LazyChatMarkdown>{content}</LazyChatMarkdown>
          </div>
        ) : isError ? (
          <p className="m-0 text-sm text-muted-foreground">
            Notes aren&rsquo;t available for this run.
          </p>
        ) : (
          <p className="m-0 text-sm text-muted-foreground">
            {sectionId !== null
              ? 'The agent hasn\u2019t written notes for this section yet.'
              : 'The agent hasn\u2019t written its notes yet.'}
          </p>
        )}
      </CardContent>
    </Card>
  )
}

export default Scratchpad
